import styled, { css } from 'styled-components';
import { CloseIcon } from '@yoweb/ui/components/Icon/CloseIcon';
import { NoMediaIcon } from '@yoweb/ui/components/Icon/NoMediaIcon';
import { CheckBadge } from '@yoweb/ui/components/indicators';
import { ButtonIcon } from '@yoweb/ui/components/buttons';
import { getColor, getRadii, getZIndex, th } from '@yoweb/ui/styles/utils/theme';
import { pxToRem } from '@yoweb/ui/styles/utils';
import DocumentEmptyIcon from '@yoweb/ui/components/Icon/DocumentEmptyIcon';
import { CroppableImage } from '@yoweb/ui/components/CroppableImage';
import { Text } from '@yoweb/ui/components/typography';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";

var getEmptyIcon = function getEmptyIcon(isDocument) {
  return isDocument ? /*#__PURE__*/_jsx(DocumentEmptyIcon, {
    size: 24
  }) : /*#__PURE__*/_jsx(NoMediaIcon, {});
};
/**
 * A media image with optional badge. Defaults to a placeholder image if no image
 * src is provided.
 */


export var MediaImage = function MediaImage(_ref) {
  var image = _ref.image,
      _ref$badge = _ref.badge,
      badge = _ref$badge === void 0 ? false : _ref$badge,
      _ref$height = _ref.height,
      height = _ref$height === void 0 ? 56 : _ref$height,
      _ref$width = _ref.width,
      width = _ref$width === void 0 ? 56 : _ref$width,
      _ref$borderRadius = _ref.borderRadius,
      borderRadius = _ref$borderRadius === void 0 ? 'tiny' : _ref$borderRadius,
      deleteAction = _ref.deleteAction,
      _ref$isDocument = _ref.isDocument,
      isDocument = _ref$isDocument === void 0 ? false : _ref$isDocument,
      _ref$noMarginBottom = _ref.noMarginBottom,
      noMarginBottom = _ref$noMarginBottom === void 0 ? false : _ref$noMarginBottom,
      _ref$noBottomLeftRadi = _ref.noBottomLeftRadius,
      noBottomLeftRadius = _ref$noBottomLeftRadi === void 0 ? false : _ref$noBottomLeftRadi,
      _ref$noBottomRightRad = _ref.noBottomRightRadius,
      noBottomRightRadius = _ref$noBottomRightRad === void 0 ? false : _ref$noBottomRightRad,
      fileName = _ref.fileName;
  return /*#__PURE__*/_jsxs(Wrap, {
    children: [/*#__PURE__*/_jsx(MediaImageContainer, {
      height: height,
      width: width,
      borderRadius: borderRadius,
      noBottomLeftRadius: noBottomLeftRadius,
      noBottomRightRadius: noBottomRightRadius,
      noMarginBottom: noMarginBottom,
      "data-testid": "media-thumbnail-container",
      children: /*#__PURE__*/_jsx(MediaImageWrap, {
        noBottomLeftRadius: noBottomLeftRadius,
        noBottomRightRadius: noBottomRightRadius,
        children: image ? /*#__PURE__*/_jsx(CroppableImage, {
          image: image,
          container: {
            width: width,
            height: height
          }
        }) : /*#__PURE__*/_jsx("div", {
          children: getEmptyIcon(isDocument)
        })
      })
    }), fileName && /*#__PURE__*/_jsx(FileTitle, {
      title: fileName,
      size: "sm",
      children: fileName
    }), badge && /*#__PURE__*/_jsx(CheckBadgeWrap, {
      "data-testid": "option-checked-badge",
      children: /*#__PURE__*/_jsx(CheckBadge, {})
    }), deleteAction && /*#__PURE__*/_jsx(CloseButtonWrap, {
      children: /*#__PURE__*/_jsx(StyledButtonIcon, {
        onClick: deleteAction.onDelete,
        "aria-label": deleteAction.aria,
        icon: /*#__PURE__*/_jsx(CloseIcon, {
          size: 18
        }),
        variant: "tertiary",
        size: "sm",
        type: "button",
        radius: "full",
        "data-testid": "media-image-delete-image-button"
      })
    })]
  });
};
var Wrap = styled.div.withConfig({
  componentId: "sc-wk2zcq-0"
})(["height:100%;width:100%;position:relative;"]);
export var commonBottomBorderCss = css(["", " ", ""], function (_ref2) {
  var noBottomLeftRadius = _ref2.noBottomLeftRadius;
  return noBottomLeftRadius && css(["border-bottom-left-radius:0;"]);
}, function (_ref3) {
  var noBottomRightRadius = _ref3.noBottomRightRadius;
  return noBottomRightRadius && css(["border-bottom-right-radius:0;"]);
});
var MediaImageContainer = styled.div.withConfig({
  componentId: "sc-wk2zcq-1"
})(["height:", ";width:", ";margin-bottom:", ";overflow:hidden;position:relative;", " ", ";"], function (_ref4) {
  var height = _ref4.height;
  return height ? "".concat(height, "px") : '100%';
}, function (_ref5) {
  var width = _ref5.width;
  return width ? "".concat(width, "px") : '100%';
}, function (_ref6) {
  var noMarginBottom = _ref6.noMarginBottom;
  return noMarginBottom ? 0 : th.getSpace('small2');
}, function (_ref7) {
  var borderRadius = _ref7.borderRadius;
  return borderRadius && css(["border-radius:", ";"], getRadii(borderRadius));
}, commonBottomBorderCss);
var MediaImageWrap = styled.div.withConfig({
  componentId: "sc-wk2zcq-2"
})(["align-items:center;background-color:", ";border-radius:", ";display:flex;font-size:", ";height:100%;justify-content:center;position:relative;width:100%;", ";"], th.getColor('base050'), th.getRadii('tiny'), th.getFontSize('bodyLg'), commonBottomBorderCss);
var CheckBadgeWrap = styled.div.withConfig({
  componentId: "sc-wk2zcq-3"
})(["bottom:-", ";position:absolute;right:-", ";"], pxToRem(6), pxToRem(6));
var CloseButtonWrap = styled.div.withConfig({
  componentId: "sc-wk2zcq-4"
})(["position:absolute;top:-", ";right:-", ";border-radius:", ";z-index:", ";"], pxToRem(6), pxToRem(6), th.getRadii('full'), getZIndex('layer'));
var StyledButtonIcon = styled(ButtonIcon).withConfig({
  componentId: "sc-wk2zcq-5"
})(["&&{background-color:", ";}"], getColor('base000'));
var FileTitle = styled(Text).withConfig({
  componentId: "sc-wk2zcq-6"
})(["overflow:hidden;white-space:nowrap;text-overflow:ellipsis;"]);